import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MdEdit, MdInfo } from 'react-icons/md';
import { FaRegTrashCan } from 'react-icons/fa6';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import ModalPopUp from '@components/ModalPopUpComponent';
import {
    handleHospitalBlockDelete,
    updateTable,
    checkParentInDeviceChangeLog,
} from '@utils/hospitalUtils';
import ErrorMessage from '@components/Error/ErrorMessage';
import { IoClose } from 'react-icons/io5';
import Swal from 'sweetalert2';
import {
    capitalizeFirstLetter,
    generateUniqueTimestamp,
} from '@utils/sharedUtils';
import DeviceAssignModalPopUp from '@components/DeviceAssignModalPopupComponent';
import { useAuditLog } from '../../contexts/AuditLogContext';

interface UserTableProps {
    data: any[];
    header: any[];
    parents: any[];
    fetchData: () => void;
}

interface UserData {
    id: string;
    name: string;
    email: string;
    createdAt: string;
    deviceId: string;
    mobileNo: string;
    role: string;
    slug: string;
    tenantId: string;
    parent: string;
    parent_id: string;
}

interface FormData {
    parent: string;
    name?: string;
    id?: string;
}

const MySwal = Swal;

const HospitalDataTable = ({
    data,
    header,
    parents,
    fetchData,
}: UserTableProps) => {
    const [loading, setLoading] = useState(true);
    const [updatedData, setData] = useState(data);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeviceModal, setShowDeviceModal] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showParent, setShowParent] = useState(false);
    const [formData, setFormData] = useState<FormData>({ parent: '' });
    const [parentsData, setParentsData] = useState<any[]>([]);
    const [selectType, setSelectType] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [hospitalUserDetail, setHospitalUserDetail] = useState<any | null>(
        null
    );
    const [showTextModal, setShowTextModal] = useState(false);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [editUserData, setEditUserData] = useState<FormData | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const loggedInUserID = useAppSelector(
        (state) => state.login.loggedInUserID
    );
    const { addLog } = useAuditLog();

    useEffect(() => {
        setData(data);
    }, [data]);

    if (updatedData.length === 0) {
        return (
            <div className="card rounded-2xl w-full mx-auto p-0 !mt-5">
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="border-top m-3">
                            <tr>
                                {header.map((user) => (
                                    <th
                                        className="text-black text-base font-semibold py-4 !normal-case"
                                        key={user.id}
                                    >
                                        {user.title}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="text-center">
                                <td colSpan={header.length + 1}>
                                    {t('user_management.user_table.no_data')}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Get the keys from the first object in the data array
    let keys = Object.keys(updatedData[0]).filter(
        (key) => key !== 'id' && key !== 'parent_id' && key !== 'createdAt_time'
    );

    const handleRowClick = async (user: any) => {
       
    };

    const handleDelete = async () => {

        try {
            setLoading(true);
            const updatedBy = loggedInUserTenantID;
            if (userData) {
                // Check if the parent data is empty or if it has children
                if (!userData.parent) {
                    // Fetch all data and filter out children of the parent to be deleted
                    const children = updatedData.filter(
                        (item) => item.parent_id === userData.id
                    );

                    // Delete all children
                    for (const child of children) {
                        await handleHospitalBlockDelete(
                            loggedInUserTenantID,
                            child.id
                        );
                        addLog(
                            'Hospital Data Deleted',
                            child.id,
                            'Hospital Block',
                            loggedInUserID,
                            { 'Hospital Block': child.name, 'Parent': child.parent },
                            window.location.hostname
                        );
                    }

                    // Now delete the parent
                    await handleHospitalBlockDelete(
                        loggedInUserTenantID,
                        userData.id
                    );

                    // Update the state to remove the parent and its children
                    const updatedUserData = updatedData.filter(
                        (item) =>
                            item.id !== userData.id &&
                            item.parent_id !== userData.id
                    );
                    setData(updatedUserData);
                } else {
                    // Handle case where userData is not a parent
                    await handleHospitalBlockDelete(
                        loggedInUserTenantID,
                        userData.id
                    ).then(() => {
                        const updatedUserData = updatedData.filter(
                            (item) => item.id !== userData.id
                        );

                        setData(updatedUserData);
                    });

                    addLog(
                        'Hospital Data Deleted',
                        userData.id,
                        'Hospital Block',
                        loggedInUserID,
                        { 'Hospital Block': userData.name, 'Parent': userData.parent },
                        window.location.hostname
                    );
                }
                MySwal.fire({
                    position: 'top-end',
                    icon: 'success',
                    title: 'Data deleted successfully',
                    showConfirmButton: false,
                    timer: 1500,
                    toast: true,
                    customClass: {
                        popup: 'text-green bg-light_green',
                    },
                });
                fetchData();
                setShowDeleteModal(false);
                setShowTextModal(false);
            }
        } catch (error: any) {
            console.log('error', error);
        } finally {
            setLoading(false);
        }
    };

    const checkDeviceAssignData = async (user: any) => {
        setUserData(user);
        setShowDeviceModal(false);
        setShowDeleteModal(false);

        if (user) {
            // Function to recursively check for children
            const checkChildren = async (id: string): Promise<boolean> => {
                const children = updatedData.filter(
                    (item: any) => item.parent_id === id
                );

                if (children.length === 0) {
                    return false; // No children, can be deleted
                }

                for (const child of children) {
                    // Check if the child is assigned to a device
                    const changeLogData = await checkParentInDeviceChangeLog(
                        'devices',
                        child.id
                    );
                    if (changeLogData && changeLogData.length > 0) {
                        setShowDeviceModal(true);
                        return true; // Cannot be deleted
                    }

                    // Recursively check this child's children
                    const childHasIssue = await checkChildren(child.id);
                    if (childHasIssue) {
                        return true; // Cannot be deleted
                    }
                }

                return false; // All checks passed, can be deleted
            };

            // Check if the user has a parent_id
            const userDataInUpdated = updatedData.find(
                (item: any) => item.id === user.id
            );

            if (!userDataInUpdated) {
                return;
            }

            // Check if the item has children
            const hasChildren = await checkChildren(user.id);

            // Check if the item itself is assigned to a device
            const isAssignedToDevice = async (id: string) => {
                const changeLogData = await checkParentInDeviceChangeLog(
                    'devices',
                    id
                );
                return changeLogData && changeLogData.length > 0;
            };

            if (!userDataInUpdated.parent_id) {
                // No parent
                if (!hasChildren) {
                    if (await isAssignedToDevice(user.id)) {
                        setShowDeviceModal(true);
                    } else {
                        setShowDeleteModal(true);
                    }
                }
            } else {
                // Has parent
                if (!hasChildren) {
                    if (await isAssignedToDevice(user.id)) {
                        setShowDeviceModal(true);
                    } else {
                        setShowDeleteModal(true);
                    }
                } else {
                    console.log('Cannot delete, item has children.');
                    // You might want to show a different modal or message here
                }
            }
        }
    };

    const deleteUser = async (user: any) => {
        await checkDeviceAssignData(user); // Check the data before showing the modal
    };

    const handleParentChange = async (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = event.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value ? value : '',
        }));
        setError(null);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
        setError(null);
    };

    const handleAddData = (user: any) => {
        if (user.parent_id == '') {
            setShowParent(false);
        } else {
            setShowParent(true);
        }
        const filteredParents = parents.filter(
            (parent) => parent.id != user.id
        );
        setParentsData(filteredParents);
        setFormData({
            parent: user.parent_id,
            name: user.name,
            id: user.id,
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setSelectType('');
        setFormData({ parent: '' });
        setShowModal(false);
        setError(null);
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.name) {
            setError('Name is required.');
            return;
        }

        try {
            // const formattedDateTime = new Date().toISOString();
            const formattedDateTime = generateUniqueTimestamp();
            const updateExpression =
                'SET #name = :name, #parent_id = :parent_id, #modified_by = :tenant_id, #modified_at = :formattedDateTime';
            const expressionAttributeNames = {
                '#name': 'name',
                '#parent_id': 'parent_id',
                '#modified_by': 'modified_by',
                '#modified_at': 'modified_at',
            };
            const expressionAttributeValues = {
                ':name': { S: formData.name },
                ':parent_id': { S: formData.parent ? formData.parent : '' },
                ':tenant_id': { S: loggedInUserTenantID },
                ':formattedDateTime': { S: formattedDateTime },
            };

            await updateTable(
                'hospital_block',
                { uuid: { S: formData.id } },
                updateExpression,
                expressionAttributeNames,
                expressionAttributeValues
            );
            addLog(
                'Hospital Data Updated',
                formData.id ? formData.id : '',
                'Hospital Block',
                loggedInUserID,
                { 'Hospital Block': formData.name, 'Parent': formData.parent ? formData.parent : '' },
                window.location.hostname
            );
            MySwal.fire({
                position: 'top-end', // Position the toast in the top-right corner
                icon: 'success',
                title: 'Data updated successfully',
                showConfirmButton: false,
                timer: 1500,
                toast: true,
                customClass: {
                    popup: 'text-green bg-light_green', // Add custom class to the popup
                },
            });

            const parentObject = data.find(
                (item) => item.id == formData.parent
            );
            const updated = updatedData.map((parentData) =>
                parentData.id === formData.id
                    ? {
                          ...parentData,
                          parent: parentObject ? parentObject.name : '',
                          name: formData.name
                              ? capitalizeFirstLetter(formData.name)
                              : '',
                      }
                    : parentData
            );
            setData(updated);
            setShowModal(false);
            setError(null);
            setSelectType('');
            setFormData({ parent: '' });
        } catch (error) {
            console.error('Error saving data to DynamoDB:', error);
            setError('Error saving data. Please try again.');
        }
    };

    return (
        <>
            <div className="card rounded-2xl w-full mx-auto p-0 !mt-5">
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="m-3">
                            <tr>
                                {header.map((user) => (
                                    <th
                                        className="text-black text-base font-semibold py-3 !normal-case"
                                        key={user.id}
                                    >
                                        {user.title}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {updatedData.map((user) => (
                                <tr key={user.id}>
                                    {/* <td></td> */}
                                    {keys.map((key) => {
                                        const value = user[key];
                                        const isStatusKey =
                                            key.toLowerCase().trim() ===
                                            'status';
                                        return (
                                            <td
                                                key={key}
                                                onClick={() =>
                                                    handleRowClick(user)
                                                }
                                                className="cursor-pointer"
                                            >
                                                <div className="text-base font-normal text-table_header">
                                                    {value ?? 'N/A'}
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td>
                                        <div className="flex space-x-5 items-center">
                                            <div
                                                className="cursor-pointer"
                                                onClick={() =>
                                                    handleAddData(user)
                                                }
                                            >
                                                <MdEdit className="text-black" />
                                            </div>

                                            <div
                                                className="cursor-pointer"
                                                onClick={() => deleteUser(user)}
                                            >
                                                <FaRegTrashCan className="text-danger" />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            <tr></tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <ModalPopUp
                show={showDeleteModal}
                message={t('pop_up.hospital_data_delete_msg')}
                onConfirm={handleDelete}
                onCancel={() => setShowDeleteModal(false)}
            />
            <DeviceAssignModalPopUp
                show={showDeviceModal}
                message={t('pop_up.device_assign_msg')}
                onConfirm={() => {
                    setShowDeviceModal(false);
                }}
                onCancel={() => setShowDeviceModal(false)}
            />

            {showModal && (
                <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-gray-800 bg-opacity-50 z-50">
                    <div className="bg-white rounded-lg p-6 w-96 relative">
                        <h2 className="text-lg font-semibold mb-3 text-center text-black">
                            {t('management.hospital.update_data')}
                        </h2>
                        <span
                            className="absolute right-0 top-5 cursor-pointer"
                            onClick={handleCloseModal}
                        >
                            <span className="absolute text-xs top-2 right-10 cursor-pointer text-black">
                                {t('hospital.close')}
                            </span>
                            <span className="absolute top-2 right-5 cursor-pointer text-black">
                                <IoClose />
                            </span>
                        </span>
                        <div className="bg-white w-64 mx-auto">
                            <div className="mb-4">
                                <label
                                    htmlFor="name"
                                    className="block mb-1 text-sm font-normal"
                                >
                                    {t('hospital.name')}
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    className="w-full border rounded p-2 placeholder-gains_boro"
                                    placeholder="Enter The Name"
                                    value={formData.name || ''}
                                    onChange={handleInputChange}
                                />
                                <div className="relative row mt-2">
                                    <MdInfo className="text-secondary col-2 px-0 text-base text-center mt-3" />
                                    <span className="text-sm font-normal col-10 px-0">
                                        {t('hospital.discription')}
                                    </span>
                                </div>
                            </div>
                            {parentsData && parentsData.length != 0 && showParent && (
                                <div className="mb-4">
                                    <label
                                        htmlFor="parent"
                                        className="block mb-1"
                                    >
                                        {t('hospital.select_parent')}
                                    </label>
                                    <select
                                        id="parent"
                                        name="parent"
                                        className="w-full border rounded p-2"
                                        value={formData.parent || ''}
                                        onChange={handleParentChange}
                                    >
                                        <option value="">
                                            {t('hospital.select')}
                                        </option>
                                        {parentsData.map((parent) => (
                                            <option
                                                key={parent.id}
                                                value={parent.id}
                                            >
                                                {parent.name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="relative row mt-2">
                                        <MdInfo className="text-secondary col-2 px-0 text-lg text-center mt-3" />
                                        <span className="text-sm font-normal col-10 px-0">
                                            {t('hospital.parent_discription')}
                                        </span>
                                    </div>
                                </div>
                            )}
                            {error && <ErrorMessage message={error} />}
                            <button
                                className="bg-primary text-white py-2 px-4 rounded cursor-pointer w-full"
                                onClick={handleSubmit}
                            >
                                {t('management.hospital.update')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default HospitalDataTable;
